import React, { useEffect, useRef, useState } from 'react'
// `/pure`: lo script di Stripe parte solo quando si paga, non a ogni pagina
// (guardia noExternalRequests di LeaRes, on-prem compreso).
import { loadStripe } from '@stripe/stripe-js/pure'
import { useTranslation } from 'react-i18next'

/**
 * Avvia la Checkout incorporata di Stripe (Managed Payments, spec digiscan-web
 * 2026-10-22). Lo Stripe.js v3 che carichiamo oggi (@stripe/stripe-js 5) la
 * chiama `initEmbeddedCheckout`; quello dahlia l'ha rinominata
 * `createEmbeddedCheckoutPage`. Si usa quella che c'e'.
 *
 * @param {object} stripe - istanza restituita da loadStripe
 * @param {string} clientSecret - della Checkout Session creata dal server
 * @returns {Promise<{mount: Function, destroy: Function}>}
 */
export const startEmbeddedCheckout = (stripe, clientSecret) => {
  const create =
    stripe.createEmbeddedCheckoutPage || stripe.initEmbeddedCheckout
  if (typeof create !== 'function') {
    throw new Error('This Stripe.js does not support the embedded Checkout')
  }
  return create.call(stripe, {
    fetchClientSecret: () => Promise.resolve(clientSecret),
  })
}

/**
 * La Checkout di Stripe dentro la stazione di ricarica, al posto del modulo
 * carta. Stripe porta il browser su `return_url` a pagamento concluso; il
 * pulsante «indietro» chiude il pannello e distrugge la Checkout (ne puo'
 * esistere una sola per pagina).
 */
const EmbeddedCheckoutPanel = ({
  publishableKey,
  clientSecret,
  onBack,
  title,
}) => {
  const container = useRef(null)
  const [state, setState] = useState('loading')
  const { t } = useTranslation('payments')

  useEffect(() => {
    let closed = false
    let checkout = null
    if (!publishableKey || !clientSecret) {
      setState('error')
      return undefined
    }
    setState('loading')
    ;(async () => {
      try {
        const stripe = await loadStripe(publishableKey)
        if (!stripe) throw new Error('Stripe.js not loaded')
        const instance = await startEmbeddedCheckout(stripe, clientSecret)
        if (closed) {
          instance.destroy()
          return
        }
        checkout = instance
        instance.mount(container.current)
        setState('ready')
      } catch (error) {
        if (closed) return
        // Stripe.js bloccato, chiave sbagliata, sessione scaduta: l'utente vede
        // l'errore in pagina, il dettaglio resta nella console per chi indaga.
        console.error('Embedded Checkout could not start:', error)
        setState('error')
      }
    })()
    return () => {
      closed = true
      if (checkout) checkout.destroy()
    }
  }, [publishableKey, clientSecret])

  return (
    <div className="flex flex-col h-full min-h-0 gap-3">
      <div className="flex items-center justify-between gap-3 flex-shrink-0">
        <h2 className="text-xl">{title || t('checkout.embedded.title')}</h2>
        <button
          type="button"
          data-testid="embedded-checkout-back"
          onClick={onBack}
          className="rounded-xl border-2 border-current px-4 py-1 text-sm"
        >
          {t('checkout.embedded.back')}
        </button>
      </div>
      {state === 'loading' && (
        <p className="text-sm opacity-70" role="status">
          {t('checkout.embedded.loading')}
        </p>
      )}
      {state === 'error' && (
        <p
          data-testid="embedded-checkout-error"
          className="p-3 rounded-lg bg-red/10 text-red text-sm"
        >
          {t('checkout.embedded.loadError')}
        </p>
      )}
      {/* Stripe disegna qui il suo iframe: sfondo chiaro, e' il loro tema. */}
      <div
        ref={container}
        data-testid="embedded-checkout"
        className="flex-1 min-h-0 overflow-auto rounded-xl bg-white"
      />
    </div>
  )
}

export default EmbeddedCheckoutPanel
